import React, { useState, useEffect, useCallback } from 'react';
import { logisticsApi } from '../../network/apis/logistics';
import '../../assets/css/logistics/rates.css';

const LogisticsRates = () => {
    const [loading, setLoading] = useState(true);
    const [rates, setRates] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingRate, setEditingRate] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        origin_state: '',
        destination_state: '',
        base_price: '',
        price_per_kg: '',
        min_weight: '',
        max_weight: '',
        estimated_days: '',
        is_active: true
    });

    const nigerianStates = [
        'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 
        'Borno', 'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT',
        'Gombe', 'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi',
        'Kwara', 'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo',
        'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'
    ];

    const fetchRates = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await logisticsApi.getRates();
            const data = response.data?.data || response.data || [];
            setRates(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch rates:', err);
            setError(err.response?.data?.message || 'Failed to load rates');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRates();
    }, [fetchRates]);

    const resetForm = () => {
        setFormData({
            name: '',
            origin_state: '',
            destination_state: '',
            base_price: '',
            price_per_kg: '',
            min_weight: '',
            max_weight: '',
            estimated_days: '',
            is_active: true
        });
        setEditingRate(null);
    };

    const openCreateModal = () => {
        resetForm();
        setShowModal(true);
    };

    const openEditModal = (rate) => {
        setEditingRate(rate);
        setFormData({
            name: rate.name || '',
            origin_state: rate.origin_state || '',
            destination_state: rate.destination_state || '',
            base_price: rate.base_price || '',
            price_per_kg: rate.price_per_kg || '',
            min_weight: rate.min_weight || '',
            max_weight: rate.max_weight || '',
            estimated_days: rate.estimated_days || '',
            is_active: rate.is_active !== false
        });
        setShowModal(true);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            setError('');

            const payload = {
                ...formData,
                base_price: parseFloat(formData.base_price) || 0,
                price_per_kg: parseFloat(formData.price_per_kg) || 0,
                min_weight: parseFloat(formData.min_weight) || 0,
                max_weight: parseFloat(formData.max_weight) || null,
                estimated_days: parseInt(formData.estimated_days) || 1
            };

            if (editingRate) {
                await logisticsApi.updateRate(editingRate.id, payload);
                setRates(prev => prev.map(r => 
                    r.id === editingRate.id ? { ...r, ...payload } : r
                ));
                setSuccess('Rate updated successfully');
            } else {
                const response = await logisticsApi.createRate(payload);
                const newRate = response.data?.data || response.data;
                setRates(prev => [...prev, newRate]);
                setSuccess('Rate created successfully');
            }

            setShowModal(false);
            resetForm();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save rate');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (rate) => {
        if (!window.confirm(`Delete rate "${rate.name}"? This action cannot be undone.`)) {
            return;
        }

        try {
            await logisticsApi.deleteRate(rate.id);
            setRates(prev => prev.filter(r => r.id !== rate.id));
            setSuccess('Rate deleted successfully');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete rate');
            setTimeout(() => setError(''), 3000);
        }
    };

    const formatPrice = (price) => {
        return new window.Intl.NumberFormat('en-NG', {
            style: 'currency',
            currency: 'NGN',
            minimumFractionDigits: 0
        }).format(price);
    };

    return (
        <div className="logistics-rates">
            <header className="page-header">
                <h1>Shipping Rates</h1>
                <button className="btn-add" onClick={openCreateModal}>
                    + Add New Rate
                </button>
            </header>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            {loading ? (
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading rates...</p>
                </div>
            ) : rates.length === 0 ? (
                <div className="empty-state">
                    <h3>No rates configured</h3>
                    <p>Create shipping rates to allow customers to ship with you</p>
                    <button className="btn-add" onClick={openCreateModal}>
                        + Add Your First Rate
                    </button>
                </div>
            ) : (
                <div className="rates-grid">
                    {rates.map(rate => (
                        <div key={rate.id} className="rate-card">
                            <div className="rate-header">
                                <div>
                                    <h3 className="rate-name">{rate.name}</h3>
                                    <p className="rate-type">
                                        {rate.origin_state} → {rate.destination_state}
                                    </p>
                                </div>
                                <span className={`rate-status ${rate.is_active ? 'active' : 'inactive'}`}>
                                    {rate.is_active ? 'Active' : 'Inactive'}
                                </span>
                            </div>

                            <div className="rate-price">
                                {formatPrice(rate.base_price)}
                                <small> base</small>
                            </div>

                            <div className="rate-details">
                                <div className="rate-row">
                                    <span className="rate-label">Per kg</span>
                                    <span className="rate-value">{formatPrice(rate.price_per_kg)}</span>
                                </div>
                                <div className="rate-row">
                                    <span className="rate-label">Weight Range</span>
                                    <span className="rate-value">
                                        {rate.min_weight}kg - {rate.max_weight ? `${rate.max_weight}kg` : 'No limit'}
                                    </span>
                                </div>
                                <div className="rate-row">
                                    <span className="rate-label">Est. Delivery</span>
                                    <span className="rate-value">{rate.estimated_days} day(s)</span>
                                </div>
                            </div>

                            <div className="rate-actions">
                                <button 
                                    className="btn-edit"
                                    onClick={() => openEditModal(rate)}
                                >
                                    Edit
                                </button>
                                <button 
                                    className="btn-delete"
                                    onClick={() => handleDelete(rate)}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>{editingRate ? 'Edit Rate' : 'Add New Rate'}</h2>
                        
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Rate Name *</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="e.g., Lagos Express, Intra-State Standard"
                                    required
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Origin State *</label>
                                    <select
                                        name="origin_state"
                                        value={formData.origin_state}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Select...</option>
                                        {nigerianStates.map(state => (
                                            <option key={state} value={state}>{state}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Destination State *</label>
                                    <select
                                        name="destination_state"
                                        value={formData.destination_state}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Select...</option>
                                        {nigerianStates.map(state => (
                                            <option key={state} value={state}>{state}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Base Price (₦) *</label>
                                    <input
                                        type="number"
                                        name="base_price"
                                        value={formData.base_price}
                                        onChange={handleInputChange}
                                        min="0"
                                        step="0.01"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Price Per kg (₦) *</label>
                                    <input
                                        type="number"
                                        name="price_per_kg"
                                        value={formData.price_per_kg}
                                        onChange={handleInputChange}
                                        min="0"
                                        step="0.01"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Min Weight (kg)</label>
                                    <input
                                        type="number"
                                        name="min_weight"
                                        value={formData.min_weight}
                                        onChange={handleInputChange}
                                        min="0"
                                        step="0.1"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Max Weight (kg)</label>
                                    <input
                                        type="number"
                                        name="max_weight"
                                        value={formData.max_weight}
                                        onChange={handleInputChange}
                                        min="0"
                                        step="0.1"
                                        placeholder="No limit"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Estimated Delivery (days) *</label>
                                <input
                                    type="number"
                                    name="estimated_days"
                                    value={formData.estimated_days}
                                    onChange={handleInputChange}
                                    min="1"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label className="checkbox-group">
                                    <input
                                        type="checkbox"
                                        name="is_active"
                                        checked={formData.is_active}
                                        onChange={handleInputChange}
                                    />
                                    Active (visible to customers)
                                </label>
                            </div>

                            <div className="modal-actions">
                                <button
                                    type="button"
                                    className="btn-cancel"
                                    onClick={() => setShowModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn-submit"
                                    disabled={saving}
                                >
                                    {saving ? 'Saving...' : editingRate ? 'Update Rate' : 'Create Rate'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LogisticsRates;
